<script setup lang="ts" generic="T extends SelectInputType">
import type {
  Ref,
} from 'vue';
import { useTranslation } from 'i18next-vue';
import { debounce } from 'lodash-es';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';

import type {
  IFormPlainDataListboxItem,
  SelectInputType,
} from '@modules/forms/types';
import VLoader from '@components/loader/VLoader.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import VFormCheckbox from '@modules/forms/components/inputs/VFormCheckbox/VFormCheckbox.vue';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';
import VFormPlainDataListboxFooter
  from '@modules/forms/components/inputs/VFormPlainDataListboxFooter/VFormPlainDataListboxFooter.vue';
import { FORM_INPUT_DEBOUNCE_DEFAULT_MS, TRANSLATION_GROUP_DROPDOWN } from '@modules/forms/constants';
import { isBoolean, isNumber, isString } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type { IFormPlainDataListboxEmits, IFormPlainDataListboxProps, IFormPlainDataListboxSlots } from './types';
import {

  ListboxSortType,
} from './types';

const props = withDefaults(defineProps<IFormPlainDataListboxProps<T>>(), {
  data: () => ([]),
  sortButton: true,
  messageSeverity: 'info',
});
const emit = defineEmits<IFormPlainDataListboxEmits<T>>();

defineSlots<IFormPlainDataListboxSlots>();

const { t, i18next } = useTranslation();

const checkedLength = ref(0);
const searchQuery = ref('');
const sortType = ref<ListboxSortType>(ListboxSortType.DEFAULT);
const isListReady = ref(false);

const localListBackup = ref<IFormPlainDataListboxItem<T>[]>([]) as unknown as Ref<IFormPlainDataListboxItem<T>[]>;
const localList = ref<IFormPlainDataListboxItem<T>[]>([]) as unknown as Ref<IFormPlainDataListboxItem<T>[]>;

const filteredData = computed(() => (
  searchQuery.value
    ? localList.value.filter((item) => String(item.value ?? item.name)?.toLowerCase().includes(searchQuery.value.toLowerCase()))
    : localList.value
));
const totalLength = computed(() => filteredData.value.length);

function updateCounters(): void {
  checkedLength.value = filteredData.value.filter((item) => item.check).length;
}

async function onSearchQuery(): Promise<void> {
  await nextTick();
  updateCounters();
}

const debouncedSearch = debounce(onSearchQuery, FORM_INPUT_DEBOUNCE_DEFAULT_MS);

function onSearchBlur(): void {
  void debouncedSearch.flush();
}

function emitChange(value: T | string, check: boolean): void {
  emit('change', {
    value,
    check,
    values: localList.value.filter((item) => item.check),
    search: searchQuery.value || undefined,
  });
}

function onChangeAll(check: boolean, force?: boolean): void {
  if (!force && isBlocked()) {
    return;
  }

  if (check) {
    checkedLength.value = filteredData.value.length;
  } else {
    checkedLength.value = 0;
  }

  for (const item of filteredData.value) {
    item.check = check;
  }

  emitChange('all', check);
}

function onCheckAll(): void {
  onChangeAll(true);
}

function onClearAll(force?: boolean): void {
  onChangeAll(false, force);
}

function onUploadFile(): void {
  if (isBlocked()) {
    return;
  }

  emit('upload-file');
}

function isBlocked(): boolean {
  return props.readonly || props.disabled;
}

function onSelectItem(option: IFormPlainDataListboxItem<T>, isChecked?: boolean): void {
  if (isBlocked()) {
    return;
  }

  checkedLength.value = isChecked
    ? (checkedLength.value + 1)
    : (checkedLength.value - 1);

  option.check = isChecked;
  emitChange(option.id, !!isChecked);

  if (props.sortAfterChangeItem) {
    void sortLocalList();
  }
}

function getOptionTranslation(option: IFormPlainDataListboxItem<T>): string {
  if (isBoolean(option.value)) {
    if (option.value && props.translationPathForTruthyValue) {
      return t(props.translationPathForTruthyValue);
    }

    if (!option.value && props.translationPathForFalsyValue) {
      return t(props.translationPathForFalsyValue);
    }

    return option.value;
  }

  if (!props.translationPath) {
    return (option.value !== undefined ? String(option.value) : option.value) ?? option.name ?? '';
  }

  return t(`${props.translationPath}${option.id}`);
}

function sortPlainDataListboxItems(a: IFormPlainDataListboxItem<T>, b: IFormPlainDataListboxItem<T>): number {
  if (isNumber(a.value) && isNumber(b.value)) {
    return a.value - b.value;
  }

  if (isString(a.value) && isString(b.value)) {
    const parsedA = Number.parseInt(a.value, 10);
    const parsedB = Number.parseInt(b.value, 10);

    if (Number.isNaN(parsedA) || Number.isNaN(parsedB)) {
      if (a.value.toLocaleLowerCase() < b.value.toLocaleLowerCase()) {
        return -1;
      }
      return 0;
    }
    return parsedA - parsedB;
  }

  return 0;
}

function sortPlainDataList(backupList: IFormPlainDataListboxItem<T>[]): IFormPlainDataListboxItem<T>[] {
  const checked: IFormPlainDataListboxItem<T>[] = [];
  const rest: IFormPlainDataListboxItem<T>[] = [];

  for (const item of backupList) {
    if (item.check) {
      checked.push(item);
    } else {
      rest.push(item);
    }
  }

  checked.sort(sortPlainDataListboxItems);
  rest.sort(sortPlainDataListboxItems);

  return [...checked, ...rest];
}

async function sortLocalList(): Promise<void> {
  isListReady.value = false;

  await nextTick();

  if (sortType.value === ListboxSortType.CHECKED && props.sortButton) {
    localList.value = sortPlainDataList(localListBackup.value);
  } else {
    localList.value = localListBackup.value;
  }

  isListReady.value = true;
}

function onSort(): void {
  sortType.value = sortType.value === ListboxSortType.DEFAULT ? ListboxSortType.CHECKED : ListboxSortType.DEFAULT;
  void sortLocalList();
}

function updateLocalizationOfValues(): void {
  for (const item of localListBackup.value) {
    item.value = getOptionTranslation(item);
  }
  void sortLocalList().then(updateCounters);
}

function clearSearchQuery(): void {
  searchQuery.value = '';
  void debouncedSearch();
}

watchImmediate(() => props.data, (newValue) => {
  localListBackup.value = newValue.map((item) => ({
    ...item,
    value: getOptionTranslation(item),
  }));

  void sortLocalList().then(updateCounters);
});

watch(() => props.resetCounter, () => {
  searchQuery.value = '';
  onClearAll(true);
});

watchImmediate(() => props.filterQuery, (newValue) => {
  searchQuery.value = newValue || '';
  void debouncedSearch();
});

onMounted(() => {
  i18next.on('localeChanged', updateLocalizationOfValues);
});

onUnmounted(() => {
  i18next.off('localeChanged', updateLocalizationOfValues);
});
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormPlainDataListbox'"
    v-data-test="{ el: 'multiselect-root' }"
    :errors="errors"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
    :hide-errors="hideErrors"
    :readonly="readonly"
  >
    <label
      v-if="label"
      class="mb-2 block text-muted-color"
    >
      {{ label }}
    </label>
    <div
      class="flex size-full flex-col"
    >
      <div
        v-data-test="{ el: 'multiselect-search' }"
        class="p-select-header w-full rounded-t-lg border-b-0 px-4 py-3"
        :class="{
          'border-0': noBorder && !isInvalid,
          'border': !noBorder || isInvalid,
          'border-error': isInvalid,
        }"
      >
        <PIconField>
          <PInputIcon class="pi pi-search" />
          <PInputText
            v-model="searchQuery"
            :placeholder="$getTranslation(TRANSLATION_GROUP_DROPDOWN, 'PLACEHOLDER')"
            :disabled="disableCheckboxFilter"
            input-id="multiselect-search-input"
            name="multiselect-search-input"
            autocomplete="off"
            class="w-full"
            @update:model-value="debouncedSearch"
            @blur="onSearchBlur"
          />
          <PInputIcon
            v-if="searchQuery && !disableCheckboxFilter"
            class="pi pi-times cursor-pointer"
            @click="clearSearchQuery"
          />
        </PIconField>
      </div>
      <template v-if="showInfoMessage">
        <div class="border-x px-4 pb-3">
          <VMessage
            :severity="messageSeverity"
            :icon="messageIcon"
            :pt="{ wrapper: { class: 'p-4' } }"
          >
            {{ infoMessage }}
          </VMessage>
        </div>
      </template>
      <div
        class="overflow-hidden rounded-b-lg border-t-0"
        :class="{
          'border-0': noBorder && !isInvalid,
          'border': !noBorder || isInvalid,
          'border-error': isInvalid,
        }"
      >
        <VLoader
          :loading="isLoading"
          min-height="240px"
        >
          <div
            v-if="!totalLength && isListReady"
            class="h-[240px] p-4 text-lg text-disabled-color"
          >
            {{ $getTranslation(TRANSLATION_GROUP_DROPDOWN, 'NO_DATA') }}
          </div>
          <PVirtualScroller
            v-else
            class="h-[240px]"
            :items="filteredData"
            :item-size="40"
            key-field="id"
          >
            <template #item="{ item }">
              <VFormCheckbox
                v-data-test="{ el: 'multiselect-item', itemId: item.id }"
                class="min-h-[40px] cursor-pointer px-4 py-3"
                :class="{
                  'pointer-events-none': disabled || readonly,
                }"
                is-full-width
                :name="String(item.id)"
                :value="item.check"
                :disabled="disabled"
                :readonly="readonly"
                hide-errors
                :label="item.value"
                @change="onSelectItem(item, $event)"
              />
            </template>
          </PVirtualScroller>
        </VLoader>
        <VFormPlainDataListboxFooter
          class="border-t border-gray-100"
          :total="totalLength"
          :checked="checkedLength"
          :disabled-buttons="disabled || readonly"
          :upload-file-btn="uploadFileBtn"
          :sort-type="sortType"
          :sort-button="sortButton"
          :sort-button-disabled="!isListReady"
          @check-all="onCheckAll"
          @clear-all="onClearAll"
          @upload-file="onUploadFile"
          @sort="onSort()"
        />
        <div
          v-if="$slots.footer"
          class="border-t border-gray-100 px-4 py-3"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
  </VFormInputWrapper>
</template>
